/**
 * Log do Assistente no front.
 *
 * Toda conversa tem um id — o `sessionId` que o agente devolve — e ele viaja
 * no cabeçalho `X-Assistente-Conversa-Id` de toda requisição do módulo. O
 * agente, o servidor MCP e o condominio vinculam esse id e publicam o que a
 * conversa registra no Stream `assistente_ia` do OpenObserve; este módulo
 * manda para lá, pelo agente (o navegador não tem as credenciais), o que
 * acontece no navegador, com a aplicação `front`. Sempre ligado, e cada
 * entrada sai na hora, numa requisição própria — sem fila nem lote: se o
 * fluxo quebra, a última entrada que chegou mostra onde.
 *
 * A única espera é a das entradas registradas antes de existir conversa (o
 * módulo montando, a sessão sendo resolvida): sem id elas não teriam o
 * `conversa_id`, então seguem assim que a primeira conversa é vinculada.
 */

export const CABECALHO_CONVERSA = 'X-Assistente-Conversa-Id'
export const CAMINHO_DOS_LOGS_DO_FRONT = '/assistente/v2/logs-do-front'
const MAXIMO_PENDENTES = 500
const MAXIMO_DADOS = 50_000

export type NivelDeLog = 'debug' | 'info' | 'warn' | 'error'

export interface EntradaDeLog {
  nivel: NivelDeLog
  origem: string
  mensagem: string
  dados?: unknown
  momento: string
  conversa_id: string | null
}

export type EnviadorDeLog = (entradas: EntradaDeLog[]) => Promise<void>

let conversaAtual: string | null = null
let pendentes: EntradaDeLog[] = []
let enviador: EnviadorDeLog | null = null
const emVoo = new Set<Promise<void>>()
const ouvintes = new Set<(conversa: string | null) => void>()

export function conversaDoAssistenteAtual (): string | null {
  return conversaAtual
}

/** Chamado quando a sessão muda. `null` desvincula (ex.: estado redefinido). */
export function definirConversaDoAssistente (id: string | null | undefined): void {
  const nova = id || null
  if (nova === conversaAtual) return
  const anterior = conversaAtual
  conversaAtual = nova
  ouvintes.forEach(ouvinte => ouvinte(nova))
  if (nova === null) return
  registrar('info', 'conversa', `conversa vinculada: ${nova}`, anterior ? { anterior } : undefined)
  const aguardando = pendentes
  pendentes = []
  aguardando.forEach(entrada => enviar({ ...entrada, conversa_id: nova }))
}

/** Assina a troca de conversa (o rodapé do chat mostra o id). */
export function aoMudarConversa (ouvinte: (conversa: string | null) => void): () => void {
  ouvintes.add(ouvinte)
  return () => ouvintes.delete(ouvinte)
}

/** ISO com o fuso local — o agente converte no `_timestamp` do documento. */
export function carimboLocal (data = new Date()): string {
  const deslocamento = -data.getTimezoneOffset()
  const sinal = deslocamento >= 0 ? '+' : '-'
  const absoluto = Math.abs(deslocamento)
  const doisDigitos = (valor: number) => String(Math.trunc(valor)).padStart(2, '0')
  const local = new Date(data.getTime() + deslocamento * 60_000).toISOString().slice(0, 23)
  return `${local}${sinal}${doisDigitos(absoluto / 60)}:${doisDigitos(absoluto % 60)}`
}

/** Erro, resposta do axios ou qualquer valor, reduzido a JSON seguro e curto. */
export function paraDados (valor: unknown): unknown {
  if (valor === undefined) return undefined
  if (valor instanceof Error) {
    const resposta = (valor as { response?: { status?: number, data?: unknown } }).response
    return {
      erro: valor.name,
      mensagem: valor.message,
      status: resposta?.status,
      corpo: resposta?.data,
      pilha: valor.stack?.split('\n').slice(0, 12).join('\n')
    }
  }
  try {
    const texto = JSON.stringify(valor)
    if (texto === undefined) return String(valor)
    if (texto.length > MAXIMO_DADOS) {
      return `${texto.slice(0, MAXIMO_DADOS)}… (+${texto.length - MAXIMO_DADOS} caracteres truncados)`
    }
    return JSON.parse(texto)
  } catch {
    return String(valor)
  }
}

export function registrar (nivel: NivelDeLog, origem: string, mensagem: string, dados?: unknown): void {
  const entrada: EntradaDeLog = {
    nivel,
    origem,
    mensagem,
    dados: paraDados(dados),
    momento: carimboLocal(),
    conversa_id: conversaAtual
  }
  if (conversaAtual) {
    enviar(entrada)
    return
  }
  pendentes.push(entrada)
  if (pendentes.length > MAXIMO_PENDENTES) pendentes.shift()
}

export interface LogDoAssistente {
  debug: (mensagem: string, dados?: unknown) => void
  info: (mensagem: string, dados?: unknown) => void
  warn: (mensagem: string, dados?: unknown) => void
  error: (mensagem: string, dados?: unknown) => void
}

export function criarLog (origem: string): LogDoAssistente {
  return {
    debug: (mensagem, dados) => registrar('debug', origem, mensagem, dados),
    info: (mensagem, dados) => registrar('info', origem, mensagem, dados),
    warn: (mensagem, dados) => registrar('warn', origem, mensagem, dados),
    error: (mensagem, dados) => registrar('error', origem, mensagem, dados)
  }
}

/** Troca quem entrega o lote ao agente (os testes usam um dublê). */
export function configurarEnvioDoLog (novo: EnviadorDeLog | null): void {
  enviador = novo
}

async function envioPadrao (entradas: EntradaDeLog[]): Promise<void> {
  // Import tardio: `@/main` importa o store, que importa este módulo.
  const { ServicoHttpAgentePtt } = await import('@/main')
  await ServicoHttpAgentePtt.post(CAMINHO_DOS_LOGS_DO_FRONT, { entradas })
}

function enviar (entrada: EntradaDeLog): void {
  const envio = (enviador ?? envioPadrao)([entrada]).catch((erro) => {
    // Sem registrar: um log sobre o envio do log viraria eco infinito. A
    // entrada se perde; o console guarda o motivo só para quem for procurar.
    // eslint-disable-next-line no-console
    console.debug('[assistente] o log da conversa não chegou ao agente', erro)
  })
  emVoo.add(envio)
  void envio.finally(() => emVoo.delete(envio))
}

/** Espera os envios já disparados terminarem (testes, antes de sair da página). */
export async function descarregarLogDoAssistente (): Promise<void> {
  await Promise.all([...emVoo])
}

/** Estado limpo entre testes. */
export function redefinirLogDoAssistente (): void {
  conversaAtual = null
  pendentes = []
  enviador = null
  emVoo.clear()
  ouvintes.clear()
}
